import apiClient, { API_ENDPOINTS } from './config';
import { RepositorySummary } from '../types/project';
import { RepositoryResultsPayload, RepositoryTestGenerationSummary } from '../types/repository';

interface AzureRepoUrlRecord {
  repository_id: number;
  project_id?: number | null;
  repository_name: string;
  azure_repo_url?: string | null;
}

interface AzureRepoUrlLookupResponse {
  count: number;
  items: AzureRepoUrlRecord[];
}

export const fetchRepositories = async (): Promise<RepositorySummary[]> => {
  const response = await apiClient.get<RepositorySummary[]>(API_ENDPOINTS.REPOSITORIES);
  return response.data || [];
};

export const fetchRepositoryDetails = async (
  repositoryId: number | string,
): Promise<RepositorySummary> => {
  const response = await apiClient.get<RepositorySummary>(API_ENDPOINTS.REPOSITORY_DETAILS(String(repositoryId)));
  return response.data;
};

export const queueTestGeneration = async (
  repositoryId: number,
  projectId?: number,
): Promise<{ id: number; repository_id: number; project_id?: number; status: string; progress: number; created_at: string }> => {
  const response = await apiClient.post(
    API_ENDPOINTS.GENERATE_TESTS(String(repositoryId)),
    {
      repository_id: repositoryId,
      project_id: projectId,
    },
  );
  return response.data;
};

export const deleteRepository = async (repositoryId: number | string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.REPOSITORY_DETAILS(String(repositoryId)));
};

export const downloadRepositoryZip = async (
  endpoint: string,
): Promise<Blob> => {
  const response = await apiClient.get(endpoint, { responseType: 'blob' });
  return response.data;
};

export const fetchRepositoryLogs = async (repositoryId: number | string) => {
  const response = await apiClient.get(API_ENDPOINTS.REPOSITORY_LOGS(String(repositoryId)));
  return response.data;
};

export const fetchRepositoryInputTree = async (repositoryId: number | string) => {
  const response = await apiClient.get(API_ENDPOINTS.REPOSITORY_INPUT_FILES(String(repositoryId)));
  return response.data || [];
};

export const fetchRepositoryOutputTree = async (repositoryId: number | string) => {
  const response = await apiClient.get(API_ENDPOINTS.REPOSITORY_OUTPUT_FILES(String(repositoryId)));
  return response.data || [];
};

export const fetchRepositoryMetrics = async (repositoryId: number | string) => {
  const response = await apiClient.get(API_ENDPOINTS.REPOSITORY_METRICS(String(repositoryId)));
  return response.data || null;
};

export const fetchRepositoryResults = async (
  repositoryId: number | string,
  options?: { refresh?: boolean },
): Promise<RepositoryResultsPayload> => {
  const endpoint = API_ENDPOINTS.REPOSITORY_RESULTS(String(repositoryId));
  const url = options?.refresh ? `${endpoint}?refresh=true` : endpoint;
  const response = await apiClient.get<RepositoryResultsPayload>(url);
  return response.data;
};

export const refreshRepositoryResults = async (
  repositoryId: number | string,
): Promise<RepositoryResultsPayload> => {
  const response = await apiClient.post<RepositoryResultsPayload>(
    API_ENDPOINTS.REPOSITORY_RESULTS_REFRESH(String(repositoryId)),
  );
  return response.data;
};

export const fetchRepositoryTestGenerations = async (
  repositoryId: number | string,
): Promise<RepositoryTestGenerationSummary[]> => {
  const response = await apiClient.get<RepositoryTestGenerationSummary[]>(
    API_ENDPOINTS.REPOSITORY_TEST_GENERATIONS(String(repositoryId)),
  );
  return response.data || [];
};

export const fetchRepositoryFileContent = async (
  repositoryId: number | string,
  scope: 'input' | 'output',
  path: string,
) => {
  const response = await apiClient.get(
    API_ENDPOINTS.REPOSITORY_FILE_CONTENT(String(repositoryId), scope, path),
  );
  return response.data;
};

export const fetchAzureRepoUrl = async (
  options: { projectId?: number; repositoryId?: number },
): Promise<string | null> => {
  const params: { project_id?: number; repository_id?: number } = {};
  if (options.projectId != null) {
    params.project_id = options.projectId;
  }
  if (options.repositoryId != null) {
    params.repository_id = options.repositoryId;
  }

  const response = await apiClient.get<AzureRepoUrlLookupResponse>(
    API_ENDPOINTS.REPOSITORY_AZURE_REPO_URL,
    { params },
  );

  const items = response.data?.items ?? [];
  const firstWithUrl = items.find((item) => Boolean(item.azure_repo_url));
  return firstWithUrl?.azure_repo_url ?? null;
};
